import { useLocation, useNavigate } from "react-router-dom";
import {
  Drawer,
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
  alpha,
} from "@mui/material";
import * as Icons from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import {
  NAV_ITEMS,
  SETTINGS_NAV,
  DRAWER_WIDTH,
  type NavItem,
} from "./constants";

interface SidebarProps {
  open: boolean;
  onClose: () => void;
}

function NavIcon({ name }: { name: keyof typeof Icons }) {
  const IconComponent = Icons[name] as React.ElementType;
  return IconComponent ? <IconComponent fontSize="small" /> : null;
}

export default function Sidebar({ open, onClose }: SidebarProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  const handleNav = (item: NavItem) => {
    navigate(item.path);
    if (!isDesktop) onClose();
  };

  const settingsActive =
    location.pathname === SETTINGS_NAV.path ||
    location.pathname.startsWith(SETTINGS_NAV.path + "/");

  const drawerContent = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        maxHeight: "100vh",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          bgcolor: (th) =>
            th.palette.mode === "dark"
              ? th.palette.grey[900]
              : th.palette.primary.main,
          color: "#fff",
          py: 2,
          px: 2,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          minHeight: 64,
          cursor: "pointer",
        }}
        onClick={() => {
          navigate("/dashboard");
          if (!isDesktop) onClose();
        }}
      >
        <Icons.AdminPanelSettings />
        <Typography
          variant="h6"
          noWrap
          sx={{
            fontWeight: 700,
          }}
        >
          {t("app.short")}
        </Typography>
      </Box>

      <List sx={{ flex: 1, pt: 1.5, px: 0.5, overflowY: "auto" }}>
        {NAV_ITEMS.map((item) => {
          const isActive =
            item.path === "/dashboard"
              ? location.pathname === "/dashboard"
              : location.pathname === item.path ||
                location.pathname.startsWith(item.path + "/");
          return (
            <ListItem key={item.path} disablePadding sx={{ mb: 0.25 }}>
              <ListItemButton
                selected={isActive}
                onClick={() => handleNav(item)}
                sx={{ py: 1, px: 2 }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <NavIcon name={item.icon} />
                </ListItemIcon>
                <ListItemText
                  primary={t(item.labelKey)}
                  slotProps={{ primary: { sx: { fontSize: "0.875rem" } } }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
        <ListItem disablePadding sx={{ mb: 0.25 }}>
          <ListItemButton
            component="a"
            href="https://bugs.pinehood.tech/"
            target="_blank"
            rel="noopener noreferrer"
            sx={{ py: 1, px: 2 }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <Icons.BugReport fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={t("nav.reportBug")}
              slotProps={{ primary: { sx: { fontSize: "0.875rem" } } }}
            />
          </ListItemButton>
        </ListItem>
      </List>

      <Box sx={{ flexShrink: 0, px: 0.5, pb: 0.5 }}>
        <ListItem disablePadding>
          <ListItemButton
            selected={settingsActive}
            onClick={() => handleNav(SETTINGS_NAV)}
            sx={{ py: 1, px: 2 }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <NavIcon name={SETTINGS_NAV.icon} />
            </ListItemIcon>
            <ListItemText
              primary={t(SETTINGS_NAV.labelKey)}
              slotProps={{ primary: { sx: { fontSize: "0.875rem" } } }}
            />
          </ListItemButton>
        </ListItem>
      </Box>

      <Box
        sx={{
          flexShrink: 0,
          py: 1.5,
          px: 2,
          bgcolor: (th) =>
            th.palette.mode === "dark"
              ? th.palette.grey[900]
              : th.palette.primary.main,
          color: "#fff",
        }}
      >
        <Typography
          variant="caption"
          sx={{
            display: "block",
            textAlign: "center",
            opacity: 0.7,
          }}
        >
          v0.0.1
        </Typography>
      </Box>
    </Box>
  );

  const paperSx = {
    width: DRAWER_WIDTH,
    boxSizing: "border-box" as const,
    border: "none",
    boxShadow: theme.shadows[3],
  };

  if (isDesktop) {
    return (
      <Drawer
        variant="permanent"
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          "& .MuiDrawer-paper": paperSx,
        }}
      >
        {drawerContent}
      </Drawer>
    );
  }

  return (
    <Drawer
      variant="temporary"
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      sx={{ "& .MuiDrawer-paper": paperSx }}
    >
      {drawerContent}
    </Drawer>
  );
}
